import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WizardProvider } from '../engine';
import { WizardContainer } from '../components';
import { ResumeDraftModal } from '../components/ResumeDraftModal';
import { useWizardStore } from '../store';
import { getFlowForSport, getFlowById } from '../flows';
import { buildStepPath, parseStepPath } from '../engine/types';
import { isFixtureTypeAllowed } from '../blocks/fixture-config';
import { createTournament } from '@/api/tournament/tournamentApi';
import { toast } from '@/components/ui/use-toast';
import { handleGlobalError } from '@/utils/error-handler';
import { Loader2 } from 'lucide-react';
import type { ClubSchedulesSlice, TournamentDraft } from '../engine/types';
import { getDayOfWeek } from '@/types/schedule.model';
import {
  CATEGORY_RANKING_TARGET,
  TournamentFormat,
  type TournamentFormData,
} from '@/types/tournament.model';
import { effectiveAgesFromTeamConfig } from '@/utils/tournament-age';
import { useTournamentStore } from '@/store/useTournamentStore';
import { useFixedFeeAmount } from '@/hooks/useFixedFeeAmount';
import {
  fallbackTournamentDatesFromSignupDeadline,
  resolveTournamentDatesFromClubSchedulesSlice,
} from '../utils/clubScheduleDates';
import { resolveOverflowGroupClassificationForApi } from '@/utils/tournament-overflow-group';
import { defaultCountsForCategoryRanking } from '@/utils/tournament-format';
import { resolveDescriptionReglamento } from '@/utils/tournament-reglamento';

// Import registry to ensure all steps are registered
import '../registry/stepRegistry';

export function TournamentWizardPage() {
  const navigate = useNavigate();
  const { stepPath } = useParams<{ stepPath?: string }>();

  // Store
  const draft = useWizardStore((s) => s.draft);
  const hasPendingDraft = useWizardStore((s) => s.hasPendingDraft);
  const initializeDraft = useWizardStore((s) => s.initializeDraft);
  const clearDraft = useWizardStore((s) => s.clearDraft);
  const setCurrentStep = useWizardStore((s) => s.setCurrentStep);
  const setFlowId = useWizardStore((s) => s.setFlowId);
  const markStepComplete = useWizardStore((s) => s.markStepComplete);

  // Tournaments store: keep "Mis Torneos" list fresh after creating a tournament
  const refreshTournaments = useTournamentStore((s) => s.refreshTournaments);

  // Fee: backend expects base price (total - GetMatch fee)
  const { fixedFeeAmount } = useFixedFeeAmount();

  // Local state
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  // Get current flow based on draft
  const currentFlow = draft
    ? getFlowById(draft.flowId)
    : getFlowForSport(null);

  // Initialize wizard on mount.
  // NOTE: This effect is intentionally run only once (empty dependency array).
  // Adding dependencies here can re-run initialization and break URL/draft sync.
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);

      // Check if there's a pending draft from localStorage with at least 1 completed step
      const hasProgress = draft && draft.completedSteps.length > 0;
      if (hasPendingDraft && hasProgress) {
        setShowResumeModal(true);
        setIsLoading(false);
        return;
      }

      // No pending draft - check URL for step
      if (stepPath) {
        const { stepId } = parseStepPath(stepPath);

        if (draft) {
          // Draft exists, navigate to the step
          setCurrentStep(stepId);
        } else {
          // No draft, create new one
          initializeDraft();
        }
      } else {
        // No step in URL
        if (!draft) {
          // Create new draft and redirect to first step
          initializeDraft();
        }
        // Redirect to current step
        redirectToCurrentStep();
      }

      setIsLoading(false);
    };

    initialize();
  }, []);

  // Redirect to current step URL when draft changes
  const redirectToCurrentStep = useCallback(() => {
    if (!draft) return;

    const sportId = draft.sport?.sport?.uid?.toLowerCase() ?? null;
    const stepId = draft.currentStepId;
    const path = buildStepPath(sportId, stepId);

    navigate(`/crear-torneo/${path}`, { replace: true });
  }, [draft, navigate]);

  // Sync URL with current step
  useEffect(() => {
    if (!draft || isLoading) return;

    const sportId = draft.sport?.sport?.uid?.toLowerCase() ?? null;
    const expectedPath = buildStepPath(sportId, draft.currentStepId);

    if (stepPath !== expectedPath) {
      navigate(`/crear-torneo/${expectedPath}`, { replace: true });
    }
  }, [draft?.currentStepId, draft?.sport?.sport?.uid, stepPath, navigate, isLoading]);

  // Handle resume draft
  const handleResumeDraft = () => {
    setShowResumeModal(false);
    redirectToCurrentStep();
  };

  // Handle discard draft
  const handleDiscardDraft = () => {
    clearDraft();
    initializeDraft();
    setShowResumeModal(false);
    navigate('/crear-torneo/select-sport', { replace: true });
  };

  // Handle flow change (when sport is selected)
  const handleFlowChange = useCallback(
    (newFlowId: string) => {
      // Get the new flow configuration
      const newFlow = getFlowById(newFlowId);
      
      // Find the first step after sport selection (should be 'basic-info')
      const firstNonSportStep = newFlow.steps.find(step => step.id !== 'sport');
      const targetStepId = firstNonSportStep?.id ?? newFlow.steps[0]?.id ?? 'sport';
      
      // Update flow ID and current step
      setFlowId(newFlowId);
      setCurrentStep(targetStepId);
      
      // Mark sport step as completed since we have a sport selected
      markStepComplete('sport');
      
      // Navigate to the new step URL
      const sportId = newFlow.sportId?.toLowerCase() ?? null;
      const path = buildStepPath(sportId, targetStepId);
      navigate(`/crear-torneo/${path}`, { replace: true });
    },
    [setFlowId, setCurrentStep, markStepComplete, navigate]
  );

  // Handle submit
  const handleSubmit = async () => {
    if (!draft) return;

    setIsSubmitting(true);

    try {
      // Validate draft before submitting
      validateDraftForSubmit(draft);

      // Transform draft to API format (sends base price: total - GetMatch fee)
      const tournamentData = await transformDraftToApiFormat(draft, fixedFeeAmount ?? 0);

      // Create tournament
      const createdTournament = await createTournament(tournamentData);

      if (createdTournament) {
        toast({
          title: 'Torneo creado',
          description: 'El torneo se ha creado exitosamente.',
        });

        // Clear draft
        clearDraft();

        // Refresh tournaments list so "Mis Torneos" shows the new one without manual reload
        try {
          await refreshTournaments();
        } catch (e) {
          console.error('Failed to refresh tournaments after creation', e);
        }

        // Navigate to tournament details
        navigate(`/torneos/${createdTournament.id}`);
      }
    } catch (error) {
      if (error instanceof DraftValidationError) {
        // Show validation error and navigate to the problematic step
        toast({
          title: `Revisa el paso "${error.stepName}"`,
          description: error.message,
          variant: 'destructive',
        });

        // Navigate to the step that needs attention
        setCurrentStep(error.stepId);
      } else {
        handleGlobalError(
          error,
          'Error',
          'No se pudo crear el torneo. Intenta nuevamente.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-4 text-gray-600">Cargando wizard...</p>
        </div>
      </div>
    );
  }

  // Resume modal
  if (showResumeModal && draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <ResumeDraftModal
          isOpen={true}
          draft={draft}
          onResume={handleResumeDraft}
          onDiscard={handleDiscardDraft}
          onClose={() => setShowResumeModal(false)}
        />
      </div>
    );
  }

  // No draft (shouldn't happen, but handle gracefully)
  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Inicializando...</p>
        </div>
      </div>
    );
  }

  return (
    <WizardProvider
      flowConfig={currentFlow}
      onSubmit={handleSubmit}
      onFlowChange={handleFlowChange}
    >
      <WizardContainer
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        header={
          <div className="px-4 py-3">
            <h1 className="text-lg font-semibold text-gray-900">
              Crear Torneo
            </h1>
            <p className="text-sm text-gray-500">
              {currentFlow.name}
            </p>
          </div>
        }
      />
    </WizardProvider>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Validation error with step information for user feedback.
 */
class DraftValidationError extends Error {
  constructor(
    message: string,
    public stepId: string,
    public stepName: string
  ) {
    super(message);
    this.name = 'DraftValidationError';
  }
}

/**
 * Validates that all required draft data is present.
 * Throws DraftValidationError if any required field is missing.
 */
function validateDraftForSubmit(draft: TournamentDraft): void {
  const { sport, basicInfo, teamConfig, fixtureConfig, pricing, clubSelection, schedule, scheduleSelection, tournamentConfig, clubSchedules } = draft;

  // Determine which schedule mode we're using:
  // 1. New split mode: tournamentConfig + clubSchedules (separate slices)
  // 2. Legacy combined mode: scheduleSelection (single slice with all data)
  // 3. Old legacy mode: clubSelection + schedule
  const isNewSplitMode = Boolean(tournamentConfig?.signupDeadlineDate && clubSchedules?.clubSchedules?.length);
  const isLegacyCombinedMode = Boolean(scheduleSelection?.clubSchedules?.length);

  // Sport validation
  if (!sport?.sport?.uid) {
    throw new DraftValidationError(
      'Debes seleccionar un deporte',
      'sport',
      'Deporte'
    );
  }

  // Basic info validation
  if (!basicInfo?.name?.trim()) {
    throw new DraftValidationError(
      'El nombre del torneo es requerido',
      'basic-info',
      'Información'
    );
  }

  // Fixture config validation (before equipos: tipo de torneo va primero en el flujo)
  if (!fixtureConfig?.fixtureType) {
    throw new DraftValidationError(
      'Debes seleccionar el formato del torneo',
      'fixture-config',
      'Formato'
    );
  }
  if (!fixtureConfig?.format) {
    throw new DraftValidationError(
      'Debes seleccionar el tipo de torneo (corto, americano o largo)',
      'fixture-config',
      'Formato'
    );
  }
  // The step only fixes the structure when the format changes: a draft saved before that rule can
  // still carry a combination the backend can't generate (it rejects it on create too).
  if (!isFixtureTypeAllowed(fixtureConfig.format, fixtureConfig.fixtureType)) {
    throw new DraftValidationError(
      'La estructura del fixture elegida no está disponible para este tipo de torneo',
      'fixture-config',
      'Formato'
    );
  }
  // Team config validation
  if (!teamConfig?.gender) {
    throw new DraftValidationError(
      'Debes configurar el género del torneo',
      'team-config',
      'Equipos'
    );
  }
  if (!teamConfig?.playersPerTeam || teamConfig.playersPerTeam < 1) {
    throw new DraftValidationError(
      'Debes configurar los jugadores por equipo',
      'team-config',
      'Equipos'
    );
  }
  if (!teamConfig?.minCategory || !teamConfig?.maxCategory) {
    throw new DraftValidationError(
      'Debes configurar las categorías del torneo',
      'team-config',
      'Equipos'
    );
  }

  if (
    fixtureConfig?.minTeamsAllowed == null ||
    fixtureConfig?.maxTeamsAllowed == null
  ) {
    throw new DraftValidationError(
      'Debes configurar la cantidad de equipos permitidos',
      'fixture-config',
      'Formato'
    );
  }

  // Pricing validation
  if (!pricing?.price) {
    throw new DraftValidationError(
      'Debes configurar el precio del torneo',
      'pricing',
      'Precio'
    );
  }
  if (!pricing?.paymentType) {
    throw new DraftValidationError(
      'Debes seleccionar el método de pago',
      'pricing',
      'Precio'
    );
  }

  const isLongFormat = fixtureConfig?.format === TournamentFormat.LONG;

  // Club and schedule validation - depends on which mode we're using
  if (isNewSplitMode) {
    // New split mode: tournamentConfig + clubSchedules
    if (!tournamentConfig?.signupDeadlineDate) {
      throw new DraftValidationError(
        'Debes configurar la fecha de cierre de inscripción',
        'tournament-config',
        'Configuración'
      );
    }

    if (!clubSchedules?.clubSchedules?.length) {
      throw new DraftValidationError(
        'Debes seleccionar al menos un club',
        'club-schedules',
        'Clubs y Horarios'
      );
    }

    if (isLongFormat) {
      // LONG: needs the first-round start day plus the weekly grid (no per-date IDs).
      if (!clubSchedules.weekStartDate) {
        throw new DraftValidationError(
          'Debes elegir el día en que arranca la primera fecha',
          'club-schedules',
          'Clubs y Horarios'
        );
      }
      if (!clubSchedules.weeklyGridId) {
        throw new DraftValidationError(
          'Debes elegir o crear la grilla semanal de horarios',
          'club-schedules',
          'Clubs y Horarios'
        );
      }
      const gridConfig = clubSchedules.weeklyGridConfig;
      if (
        gridConfig &&
        getDayOfWeek(new Date(clubSchedules.weekStartDate + 'T00:00:00')) !== gridConfig.startDayOfWeek
      ) {
        throw new DraftValidationError(
          'La grilla elegida la comparten torneos que arrancan otro día de la semana: elegí otro día de inicio',
          'club-schedules',
          'Clubs y Horarios'
        );
      }
    } else {
      const hasSchedules = clubSchedules.clubSchedules.some(
        cs => cs.scheduleIds.length > 0
      );
      if (!hasSchedules) {
        throw new DraftValidationError(
          'Debes seleccionar al menos un bloque de horario',
          'club-schedules',
          'Clubs y Horarios'
        );
      }
    }
  } else if (isLegacyCombinedMode) {
    // Legacy combined mode: scheduleSelection with clubSchedules
    if (!scheduleSelection?.clubSchedules?.length) {
      throw new DraftValidationError(
        'Debes seleccionar al menos un club',
        'schedule-selection',
        'Horarios'
      );
    }
    
    const hasSchedules = scheduleSelection.clubSchedules.some(
      cs => cs.scheduleIds.length > 0
    );
    if (!hasSchedules) {
      throw new DraftValidationError(
        'Debes seleccionar al menos un bloque de horario',
        'schedule-selection',
        'Horarios'
      );
    }
    
    if (!scheduleSelection?.signupDeadlineDate) {
      throw new DraftValidationError(
        'Debes configurar la fecha de cierre de inscripción',
        'schedule-selection',
        'Horarios'
      );
    }
  } else {
    // Legacy mode: clubSelection + schedule
    if (!clubSelection?.selectedClub) {
      throw new DraftValidationError(
        'Debes seleccionar un club sede',
        'club-selection',
        'Sede'
      );
    }
    if (!schedule?.startDate) {
      throw new DraftValidationError(
        'Debes configurar la fecha de inicio',
        'schedule',
        'Horarios'
      );
    }
  }
}

/**
 * Calculates startDate and endDate based on the selected schedule blocks.
 * - startDate: earliest date among all selected schedules
 * - endDate: latest date among all selected schedules
 * 
 * If schedules cannot be resolved (API error or no dates), falls back to a
 * simple heuristic based on signupDeadlineDate (next day, +30 days).
 */
async function calculateTournamentDatesFromSchedules(
  draft: TournamentDraft
): Promise<{ startDate: Date; endDate: Date }> {
  const { schedule, scheduleSelection, tournamentConfig, clubSchedules } = draft;

  // Legacy single-club mode: use dates from draft.schedule directly
  if (!tournamentConfig && !clubSchedules && schedule?.schedule?.length) {
    const dates = schedule.schedule
      .flatMap((fs) => fs.schedule ?? [])
      .map((entry) => entry.date)
      .filter(Boolean);

    if (dates.length > 0) {
      const sorted = [...dates].sort();
      const startDate = new Date(sorted[0] + 'T00:00:00');
      const endDate = new Date(sorted[sorted.length - 1] + 'T00:00:00');
      return { startDate, endDate };
    }
  }

  const isNewSplitMode = Boolean(
    tournamentConfig?.signupDeadlineDate && clubSchedules?.clubSchedules?.length
  );
  const isLegacyCombinedMode = Boolean(scheduleSelection?.clubSchedules?.length);

  const fallbackDeadline =
    tournamentConfig?.signupDeadlineDate ??
    scheduleSelection?.signupDeadlineDate ??
    schedule?.signupDeadlineDate ??
    null;

  if (isNewSplitMode || isLegacyCombinedMode) {
    const source = isNewSplitMode
      ? clubSchedules!.clubSchedules
      : scheduleSelection!.clubSchedules;

    const slice: ClubSchedulesSlice = {
      clubSchedules: source,
      validForMatchDuration: null,
    };

    return resolveTournamentDatesFromClubSchedulesSlice(slice, fallbackDeadline);
  }

  return fallbackTournamentDatesFromSignupDeadline(fallbackDeadline);
}

/**
 * Transforms schedule IDs array to the format expected by the backend.
 * Backend expects: [{id: "uuid1"}, {id: "uuid2"}]
 */
function transformScheduleIdsToScheduleArray(scheduleIds: string[]): Array<{ id: string }> {
  return scheduleIds.map(id => ({ id }));
}

/**
 * Transforms the wizard draft to the API format expected by createTournament.
 * This function assumes the draft has been validated - all fields should be present.
 * Sends base price (total - GetMatch fee) so the app can add the fee on top for the player.
 */
async function transformDraftToApiFormat(
  draft: TournamentDraft,
  fixedFeeAmount: number
): Promise<TournamentFormData> {
  const { sport, basicInfo, teamConfig, fixtureConfig, pricing, clubSelection, schedule, scheduleSelection, tournamentConfig, clubSchedules } = draft;

  // Determine which schedule mode we're using:
  // 1. New split mode: tournamentConfig + clubSchedules (separate slices)
  // 2. Legacy combined mode: scheduleSelection (single slice with all data)
  // 3. Old legacy mode: clubSelection + schedule
  const isNewSplitMode = Boolean(tournamentConfig?.signupDeadlineDate && clubSchedules?.clubSchedules?.length);
  const isLegacyCombinedMode = Boolean(scheduleSelection?.clubSchedules?.length);

  // Base form data that's common to both modes
  // Price: send base (total - GetMatch fee), matching TournamentCreationForm behavior
  const agePayload = effectiveAgesFromTeamConfig(teamConfig);

  const isLongFormat = (fixtureConfig!.format ?? TournamentFormat.SHORT) === TournamentFormat.LONG;

  const baseData = {
    name: basicInfo!.name,
    // The description is written before the format is picked, so the template's
    // reglamento placeholder is resolved on the way to the API.
    description: resolveDescriptionReglamento(
      basicInfo!.description ?? '',
      fixtureConfig,
    ),
    prizes: basicInfo!.prizes ?? '',
    bannerUrl: basicInfo!.bannerUrl ?? '',
    sport: sport!.sport,
    price: {
      ...pricing!.price,
      value: pricing!.price.value - fixedFeeAmount,
    },
    type: pricing!.type,
    gender: teamConfig!.gender,
    playersPerTeam: teamConfig!.playersPerTeam,
    minTeamsAllowed: fixtureConfig!.minTeamsAllowed,
    maxTeamsAllowed: fixtureConfig!.maxTeamsAllowed,
    minAge: agePayload.minAge,
    maxAge: agePayload.maxAge,
    fixtureType: fixtureConfig!.fixtureType,
    format: fixtureConfig!.format ?? TournamentFormat.SHORT,
    teamsPerGroups: fixtureConfig!.teamsPerGroups,
    classifiedPerGroups: fixtureConfig!.classifiedPerGroups,
    minCategory: teamConfig!.minCategory,
    maxCategory: teamConfig!.maxCategory,
    forceSum: teamConfig!.forceSum,
    overflowGroupClassification: resolveOverflowGroupClassificationForApi(
      fixtureConfig,
    ),
    americanFinalBracketSize: fixtureConfig!.americanFinalBracketSize,
    americanGroupFormat: fixtureConfig!.americanGroupFormat,
    paymentType: pricing!.paymentType,
    inPause: false,
    // Weekly configuration fields for LONG tournaments
    ...(isLongFormat && tournamentConfig && {
      availabilityDeadlineDay: tournamentConfig.availabilityDeadlineDay ?? null,
      availabilityDeadlineTime: tournamentConfig.availabilityDeadlineTime ?? null,
      minSchedulesRequired: tournamentConfig.minSchedulesRequired ?? null,
      walkoversForDisqualification: tournamentConfig.walkoversForDisqualification ?? null,
      autoPublishRounds: tournamentConfig.autoPublishRounds ?? false,
    }),
    // Tennis/squash use the legacy flow, where tournamentConfig is null.
    countsForCategoryRanking:
      tournamentConfig?.countsForCategoryRanking ?? defaultCountsForCategoryRanking(fixtureConfig?.format),
    categoryRankingTarget:
      tournamentConfig?.categoryRankingTarget ?? CATEGORY_RANKING_TARGET.PLAYER_CATEGORY,
    categoryRankingCategory:
      tournamentConfig?.categoryRankingTarget === CATEGORY_RANKING_TARGET.FIXED_CATEGORY
        ? (tournamentConfig?.categoryRankingCategory ?? undefined)
        : undefined,
  };

  if (isNewSplitMode) {
    // New split mode: use tournamentConfig + clubSchedules
    const clubScheduleData = clubSchedules!.clubSchedules;
    const signupDeadlineDate = tournamentConfig!.signupDeadlineDate;
    const { startDate, endDate } = await calculateTournamentDatesFromSchedules(
      draft
    );

    const clubs = clubScheduleData.map(cs => cs.club);

    if (isLongFormat) {
      // LONG: the chosen first-round start day defines startDate and the weekly grid goes by id. endDate
      // stays a placeholder — the backend materializes the real round dates from the grid.
      const longStartDate = clubSchedules!.weekStartDate
        ? new Date(clubSchedules!.weekStartDate + 'T00:00:00')
        : startDate;
      return {
        ...baseData,
        startDate: longStartDate,
        endDate,
        signupDeadlineDate,
        matchDuration: tournamentConfig!.matchDuration,
        minSchedulesRequired: tournamentConfig!.minSchedulesRequired ?? null,
        clubs: clubs,
        // The backend checks a shared grid's deadline matches; adopting it here avoids a 400 on submit.
        ...(clubSchedules!.weeklyGridConfig?.availabilityDeadlineDay &&
        clubSchedules!.weeklyGridConfig.availabilityDeadlineTime
          ? {
              availabilityDeadlineDay: clubSchedules!.weeklyGridConfig.availabilityDeadlineDay,
              availabilityDeadlineTime: clubSchedules!.weeklyGridConfig.availabilityDeadlineTime,
            }
          : {}),
        schedule: clubSchedules!.weeklyGridId ? [{ id: clubSchedules!.weeklyGridId }] : [],
      } as TournamentFormData;
    }

    const allScheduleIds = clubScheduleData.flatMap(cs => cs.scheduleIds);
    const scheduleArray = transformScheduleIdsToScheduleArray(allScheduleIds);

    return {
      ...baseData,
      startDate,
      endDate,
      signupDeadlineDate,
      matchDuration: tournamentConfig!.matchDuration,
      minSchedulesRequired: tournamentConfig!.minSchedulesRequired ?? null,
      clubs: clubs,
      schedule: scheduleArray,
    } as TournamentFormData;
  } else if (isLegacyCombinedMode) {
    // Legacy combined mode: use scheduleSelection.clubSchedules
    const clubScheduleData = scheduleSelection!.clubSchedules;
    const signupDeadlineDate = scheduleSelection!.signupDeadlineDate;
    const { startDate, endDate } = await calculateTournamentDatesFromSchedules(
      draft
    );
    
    const clubs = clubScheduleData.map(cs => cs.club);
    const allScheduleIds = clubScheduleData.flatMap(cs => cs.scheduleIds);
    const scheduleArray = transformScheduleIdsToScheduleArray(allScheduleIds);
    
    return {
      ...baseData,
      startDate,
      endDate,
      signupDeadlineDate,
      matchDuration: scheduleSelection!.matchDuration,
      clubs: clubs,
      schedule: scheduleArray,
    } as TournamentFormData;
  } else {
    // Legacy mode: use clubSelection + schedule data
    return {
      ...baseData,
      startDate: schedule!.startDate,
      endDate: schedule?.endDate ?? null,
      signupDeadlineDate: schedule?.signupDeadlineDate ?? null,
      matchDuration: schedule!.matchDuration,
      clubs: [clubSelection!.selectedClub!],
      schedule: schedule?.schedule ?? [],
    } as TournamentFormData;
  }
}

export default TournamentWizardPage;
